import PropTypes from 'prop-types';
// @flow

import React, { Component } from 'react';
import _ from 'lodash';
import keyMirror from 'keymirror';

import I18n from 'PMT/i18n/I18n';
import classNames from 'classnames';
import Constants from 'constants/Constants';

// -- routing
import Link from 'PMT/components/Link';
import Route from 'PMT/flux/Route';

import SecurityContext from 'utils/permissions/SecurityContext';
import DevelopmentUtils from 'PMT/utils/DevelopmentUtils';

// -- stores
import { RestaurantStore, RouteStore, SessionStore } from 'stores';

// -- constants
import Config from 'constants/Config';
import RoutingEnum from 'constants/RoutingEnum';

// -- entities
import Restaurant from 'PMT/entities/Restaurant';

const Menu = keyMirror({
  PAYMENT: null,
  ORDER: null,
  DASHBOARD: null,
  SETTINGS: null,
});

class AbstractMenuComponent extends Component {
  static propTypes = {
    /**
     * Cf index.js. We give to our Layout the app state, that contains the
     * current route.
     */
    routes: PropTypes.object.isRequired,
  };

  state = {
    isSectionCollapse: [],
  };

  componentDidMount() {
    // Dirty trick to display the current submenu when loading the page.
    const liParent = $('.sidebar .current')
      .parent()
      .parent();
    liParent.addClass('active');
    liParent.find('.nav-second-level').addClass('in');
    // Note: the current submenu is not put in isSectionCollapse on start, so we have to
    // double click
    // on the submenu section in order to hide it :/
  }

  // -- events

  isFeatureActivatedForSection(section): boolean {
    const restaurant: Restaurant = RestaurantStore.getRestaurant();

    if (_.isNull(restaurant)) {
      return false;
    }

    return !(
      (section === Menu.PAYMENT && (!restaurant.haveFeaturePayment() && !restaurant.haveFeatureOrder()))
      || (section === Menu.ORDER && !restaurant.haveFeatureOrder())
    );
  }

  onSectionClicked = (sections, sectionSelected) => {
    // redirect to the feature non activated page
    let shallPass = false;
    sections.map(section => {
      if (this.isFeatureActivatedForSection(section)) {
        shallPass = true;
      }
    });

    if (!shallPass) {
      RouteStore.redirectTo(RoutingEnum.FEATURE_NON_ACTIVATED);
    } else {
      const isSectionCollapse = this.state.isSectionCollapse;

      if (_.isUndefined(isSectionCollapse[sectionSelected])) {
        isSectionCollapse[sectionSelected] = false;
      } else {
        const currentValue = isSectionCollapse[sectionSelected];
        isSectionCollapse[sectionSelected] = !currentValue;
      }

      this.setState({
        isSectionCollapse,
      });
    }
  };

  // -- utils

  isActiveForRoute(route: Route): boolean {
    /**
     * Retrieve the current route.
     */
    const currentRoutes = this.props.routes;
    let currentRoute = null;
    if (currentRoutes.length > 0) {
      currentRoute = currentRoutes[currentRoutes.length - 1];
      if (_.isUndefined(currentRoute)) {
        currentRoute = null;
      }
    }

    if (_.isUndefined(currentRoute) || _.isUndefined(route)) {
      return false;
    }

    const path = currentRoute.path;
    const routeName = route.name;

    const isRestaurantMenuSubRoute =
      currentRoute.path.startsWith(RoutingEnum.RESTAURANT_MENU.path) &&
      routeName.startsWith(RoutingEnum.RESTAURANT_MENU.name);

    if (
      currentRoute.name === route.name ||
      isRestaurantMenuSubRoute ||
      // handle default route.
      (_.isNull(currentRoute.name) && route.name === RoutingEnum.DASHBOARD.name)
    ) {
      return true;
    }

    return false;
  }

  getClassForRoute(route: Route): string {
    return this.isActiveForRoute(route) ? 'current' : '';
  }

  isActiveForSection(section: number) {
    const isSectionCollapse = this.state.isSectionCollapse;

    if (_.isUndefined(isSectionCollapse[section])) {
      return false;
    }
    return isSectionCollapse[section] === false;
  }

  getClassForSection(section: number) {
    return classNames({
      // this class allows us to hide feature that or not activated for the restaurant in the
      // mobile menu
      feature_deactivated: this.isFeatureActivatedForSection(section) === false,

      // is the section the current section where we are?
      active: this.isActiveForSection(section),
    });
  }

  getClassForSecondLevel(section: number) {
    const isSectionCollapse = this.state.isSectionCollapse;
    const baseClass = 'nav nav-second-level collapse';

    if (_.isUndefined(isSectionCollapse[section])) {
      return baseClass;
    }
    return `${baseClass} ${isSectionCollapse[section] === false ? 'in' : ''}`;
  }

  updateLocale(locale: string) {
    I18n.changeLocale(locale);

    // save on session
    SessionStore.saveLocale(locale);
  }

  // -- renderers

  renderDashboard() {
    return null; // deactivate for now.
    // For the moment we handle only the booking on the dashboard, so if the restaurant haven't the
    // feature booking, we don't display iot/
    // if (!RestaurantStore.getRestaurant().haveFeatureBooking()) {
    //   return (null);
    // }
    // const mainClass = `menu_hide_big_screen ${this.getClassForSection(Menu.DASHBOARD)}`;
    //
    // return (
    //   <li className={mainClass}>
    //     <Link to={RoutingEnum.DASHBOARD}>
    //       <i className="fa fa-dashboard" />
    //       &nbsp;&nbsp;
    //       Dashboard
    //     </Link>
    //   </li>
    // );
  }

  renderPayment() {
    if (!SessionStore.isLoggedIn()) {
      return null;
    }

    const mainClass = classNames(
      'menu_hide_big_screen',
      this.getClassForSection(Menu.PAYMENT)
    );

    return (
      <li className={mainClass}>
        <a
          onClick={() =>
            this.onSectionClicked([Menu.PAYMENT, Menu.ORDER], Menu.PAYMENT)
          }
          className="bold"
        >
          <i className="fa fa-line-chart" />
          &nbsp;&nbsp;
          {I18n.tr('menu.payment')}
          <span className="fa arrow" />
        </a>
        <ul className={this.getClassForSecondLevel(Menu.PAYMENT)}>
          <li className={this.getClassForRoute(RoutingEnum.STAT_HOME)}>
            <Link to={RoutingEnum.STAT_HOME}>
              {I18n.tr('menu.payment.stat')}
            </Link>
          </li>

          <li className={this.getClassForRoute(RoutingEnum.RECEIPT)}>
            <Link to={RoutingEnum.RECEIPT}>
              {I18n.tr('menu.payment.receipt')}
            </Link>
          </li>
        </ul>
      </li>
    );
  }

  renderOrder() {
    if (!SessionStore.isLoggedIn()) {
      return null;
    }

    const mainClass = classNames(
      'menu_hide_big_screen',
      this.getClassForSection(Menu.ORDER)
    );

    return (
      <li className={mainClass}>
        <a
          onClick={() => this.onSectionClicked([Menu.ORDER], Menu.ORDER)}
          className="bold"
        >
          <i className="fa fa-shopping-cart" />
          &nbsp;&nbsp;
          {I18n.tr('menu.order')}
          <span className="fa arrow" />
        </a>
        <ul className={this.getClassForSecondLevel(Menu.ORDER)}>
          <li className={this.getClassForRoute(RoutingEnum.ORDERS_LIST)}>
            <Link to={RoutingEnum.ORDERS_LIST}>
              {I18n.tr('menu.order.orders_list')}
            </Link>
          </li>

          {SecurityContext.restaurantMenu.canRead() && (
            <li className={this.getClassForRoute(RoutingEnum.RESTAURANT_MENU)}>
              <Link to={RoutingEnum.RESTAURANT_MENU}>
                {I18n.tr('menu.order.restaurant_menu')}
              </Link>
            </li>
          )}

          {SecurityContext.restaurantMenu.canRead() && (
            <li className={this.getClassForRoute(RoutingEnum.ORDER_STATISTICS)}>
              <Link to={RoutingEnum.ORDER_STATISTICS}>
                {I18n.tr('menu.order.statistics')}
              </Link>
            </li>
          )}

          {SecurityContext.cartModifiers.canRead() && (
            <li
              className={this.getClassForRoute(
                RoutingEnum.ORDERS_CART_MODIFIERS_LIST
              )}
            >
              <Link to={RoutingEnum.ORDERS_CART_MODIFIERS_LIST}>
                {I18n.tr('menu.order.cart_modifiers')}
              </Link>
            </li>
          )}

          {SecurityContext.orderSettings.canRead() && (
            <li className={this.getClassForRoute(RoutingEnum.ORDER_SETTINGS)}>
              <Link to={RoutingEnum.ORDER_SETTINGS}>
                {I18n.tr('menu.order.settings')}
              </Link>
            </li>
          )}
        </ul>
      </li>
    );
  }

  renderRestaurantSettings() {
    if (
      !SessionStore.isLoggedIn() ||
      !SecurityContext.restaurantSettings.read()
    ) {
      return null;
    }

    const mainClass = classNames(
      'menu_hide_big_screen',
      this.getClassForSection(Menu.SETTINGS)
    );

    return (
      <li className={mainClass}>
        <a
          onClick={() => this.onSectionClicked([Menu.SETTINGS], Menu.SETTINGS)}
          className="bold"
        >
          <i className="fa fa-cog" />
          &nbsp;&nbsp;
          {I18n.tr('menu.settings')}
          <span className="fa arrow" />
        </a>
        <ul className={this.getClassForSecondLevel(Menu.SETTINGS)}>
          <li className={this.getClassForRoute(RoutingEnum.RESTAURANT_EDIT)}>
            <Link to={RoutingEnum.RESTAURANT_EDIT}>
              {I18n.tr('menu.settings.restaurant')}
            </Link>
          </li>
        </ul>
      </li>
    );
  }

  renderLocales() {
    const currentLocale = I18n.getCurrentLanguageWithoutRegionCode();
    const className = classNames('flag', currentLocale.toLowerCase());

    return (
      <li className="dropdown" id="header">
        <a
          href="#"
          className="dropdown-toggle"
          data-toggle="dropdown"
          id="header-menu-dropdown"
        >
          <i className="fa fa-globe" />
          {/* <i className={className} /> */}
        </a>

        <ul className="dropdown-menu dropdown-user">
          {Constants.supportedLocales.map(localeInfo => {
            const className = classNames({
              selected: currentLocale === localeInfo.locale,
            });

            return (
              <li
                key={localeInfo.locale}
                onClick={() => this.updateLocale(localeInfo.locale)}
                className={className}
              >
                <a href="">{localeInfo.name}</a>
              </li>
            );
          })}
        </ul>
      </li>
    );
  }

  renderTest() {
    // if (!DevelopmentUtils.isLocalhost()) {
    // return (null);
    // }

    if (!Config.IS_DEV) {
      return null;
    }

    return (
      <li className="menu_hide_big_screen">
        <Link to={RoutingEnum.TEST_PAGE}>test page</Link>
      </li>
    );
  }

  render() {
    console.error('this method should be override');
  }
}

export default AbstractMenuComponent;



// WEBPACK FOOTER //
// ./scripts/components/AbstractMenuComponent.js